import { FormEvent, useCallback, useEffect, useState } from "react";
import toast from "react-hot-toast";

import { Alert, Badge, Button, Input, Label, Spinner } from "../../../Components/ui";
import {
  HeadquartersAlimTalkSenderResponse,
  notificationCenterService,
} from "../../../api/services/notification-center.service";

const getErrorMessage = (error: any, fallback: string) => {
  const responseData = error?.response?.data;
  if (typeof responseData?.detail === "string") return responseData.detail;
  if (typeof responseData?.error === "string") return responseData.error;
  if (Array.isArray(responseData?.errors) && responseData.errors.length) {
    return responseData.errors.join(", ");
  }
  if (responseData && typeof responseData === "object") {
    const firstFieldError = Object.values(responseData).find((value) => (
      typeof value === "string" || (Array.isArray(value) && value.length)
    ));
    if (typeof firstFieldError === "string") return firstFieldError;
    if (Array.isArray(firstFieldError)) return firstFieldError.join(", ");
  }
  return error?.message || fallback;
};

const registeredSenderNumberIsValid = (value: string) => {
  const trimmed = value.trim();
  const digitCount = trimmed.replace(/\D/g, "").length;
  return /^\+?[0-9() -]+$/.test(trimmed) && digitCount >= 8 && digitCount <= 16;
};

export default function HeadquartersAlimTalkSenderSettings() {
  const [senderStatus, setSenderStatus] = useState<HeadquartersAlimTalkSenderResponse | null>(null);
  const [senderNo, setSenderNo] = useState("");
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [saving, setSaving] = useState(false);

  const applySenderStatus = useCallback((nextStatus: HeadquartersAlimTalkSenderResponse) => {
    setSenderStatus(nextStatus);
    setSenderNo(nextStatus.sender?.sender_no || "");
  }, []);

  const loadSender = useCallback(async () => {
    setLoading(true);
    setLoadError("");
    try {
      const response = await notificationCenterService.getAdminAlimTalkSender();
      applySenderStatus(response.data);
    } catch (error: any) {
      setLoadError(getErrorMessage(error, "본사 알림톡 발신번호를 불러오지 못했습니다."));
    } finally {
      setLoading(false);
    }
  }, [applySenderStatus]);

  useEffect(() => {
    void loadSender();
  }, [loadSender]);

  const saveSender = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!senderNo.trim()) {
      toast.error("DK에 등록된 본사 발신번호를 입력해 주세요.");
      return;
    }
    if (!registeredSenderNumberIsValid(senderNo)) {
      toast.error("DK에 등록된 발신 번호를 8~16자리 전화번호로 입력해 주세요.");
      return;
    }
    if (!senderStatus?.sender || saving) return;

    setSaving(true);
    try {
      const response = await notificationCenterService.updateAdminAlimTalkSender({
        sender_no: senderNo.trim(),
      });
      applySenderStatus(response.data);
      toast.success("본사 알림톡 발신번호를 저장했습니다.");
    } catch (error: any) {
      toast.error(getErrorMessage(error, "본사 알림톡 발신번호를 저장하지 못했습니다."));
    } finally {
      setSaving(false);
    }
  };

  if (loading && !senderStatus) {
    return (
      <div className="border rounded p-4 text-center" role="status">
        <Spinner size="sm" className="me-2" />
        본사 발신번호를 확인하고 있습니다.
      </div>
    );
  }

  if (loadError || !senderStatus) {
    return (
      <Alert color="danger" className="d-flex flex-wrap align-items-center justify-content-between gap-3">
        <span>{loadError || "본사 알림톡 발신번호를 불러오지 못했습니다."}</span>
        <Button color="outline-danger" size="sm" onClick={() => void loadSender()}>
          다시 시도
        </Button>
      </Alert>
    );
  }

  const senderAvailable = Boolean(senderStatus.sender);

  return (
    <div className="border rounded p-3 p-md-4">
      <div className="d-flex flex-wrap align-items-start justify-content-between gap-3 mb-3">
        <div>
          <h5 className="mb-1">본사 알림톡 발신번호</h5>
          <p className="text-muted mb-0">본사 공용 계정으로 발송할 때 사용할 번호를 관리합니다.</p>
        </div>
        <Badge color={senderStatus.sender_number_configured ? "success" : "warning"}>
          {senderStatus.sender_number_configured ? "번호 설정됨" : "번호 확인 필요"}
        </Badge>
      </div>

      <Alert color="info" className="mb-3">
        DK 관리자 화면에 등록된 본사 공용 발신번호를 확인한 뒤 동일한 번호를 입력해 주세요.
        이 화면에서는 DK 등록 번호와의 일치 여부를 자동으로 확인할 수 없습니다.
      </Alert>

      {senderStatus.sender_key_hint ? (
        <div className="d-flex flex-wrap align-items-center gap-2 mb-3">
          <span className="text-muted small">본사 발신 프로필 키</span>
          <code>{senderStatus.sender_key_hint}</code>
          {senderStatus.sender?.profile_name ? <span>{senderStatus.sender.profile_name}</span> : null}
        </div>
      ) : null}

      {senderStatus.errors?.length ? (
        <Alert color="warning" className="mb-3">
          <strong className="d-block mb-1">확인할 항목</strong>
          <ul className="mb-0 ps-3">
            {senderStatus.errors.map((error) => <li key={error}>{error}</li>)}
          </ul>
        </Alert>
      ) : null}

      {!senderAvailable ? (
        <Alert color="warning" className="mb-0">
          본사 발신 프로필이 준비되지 않아 발신번호를 변경할 수 없습니다.
        </Alert>
      ) : (
        <form onSubmit={saveSender}>
          <Label htmlFor="headquarters-alimtalk-sender-no">DK 등록 본사 발신번호</Label>
          <div className="d-flex flex-column flex-md-row gap-2 align-items-md-start">
            <div className="flex-grow-1">
              <Input
                id="headquarters-alimtalk-sender-no"
                type="tel"
                autoComplete="tel"
                value={senderNo}
                onChange={(event) => setSenderNo(event.target.value)}
                placeholder="예: 02-1234-5678"
                maxLength={24}
                disabled={saving}
                aria-required="true"
                aria-describedby="headquarters-alimtalk-sender-no-help"
              />
              <div id="headquarters-alimtalk-sender-no-help" className="form-text">
                DK에 이미 등록된 본사 공용 발신번호를 입력해 주세요.
              </div>
            </div>
            <Button color="primary" type="submit" disabled={saving}>
              {saving ? <><Spinner size="sm" className="me-2" />저장 중</> : "발신번호 저장"}
            </Button>
          </div>
        </form>
      )}
    </div>
  );
}
